/**
 * debugReportCapture.download — bundle-to-device egress for the bug-report
 * engine. Split out of `debugReportCapture.ts` (size ceiling) so the download
 * concern (filename shaping, blob egress, submit-failure rescue) lives in one
 * place. The parent module keeps the collector + submit pipeline and
 * re-exports `downloadBundle` for existing consumers.
 */
import { redactDiagnostics } from './debugReportRedact'
import type { DiagnosticData } from './debugReportCapture.types'

/**
 * Rescue download used ONLY inside the submit failure path, where the ORIGINAL
 * submit error is rethrown regardless. Its own failure must not mask that
 * original error — so it is logged loudly and reported HONESTLY through the
 * returned flag. (The old shape swallowed the download failure silently AND
 * hardcoded `bundle_downloaded=true` into the rethrown message — a lie when
 * the download had failed.)
 */
export function tryRescueDownload(report: DiagnosticData): boolean {
  try {
    downloadBundle(report)
    return true
  } catch (downloadErr) {
    console.error('[debugReport] rescue download failed while handling a submit error:', downloadErr)
    return false
  }
}

export function downloadBundle(report: DiagnosticData): void {
  // Redact on download too — a downloaded bundle is shared via Telegram / a
  // ticket, so it must be scrubbed exactly like a submitted one. Idempotent.
  const redacted = redactDiagnostics(report)
  const stamp = redacted.timestamp.replace(/[:.]/g, '-')
  // Explicit conditional: a session without an email files as 'anon' in the
  // FILENAME only (honest label for an anonymous session, not a data default).
  const safeEmail = (redacted.user.email != null ? redacted.user.email : 'anon').replace(/[^a-z0-9@.-]/gi, '_')
  const prefix = redacted.kind === 'feature' ? 'feature-request' : 'debug-report'
  const filename = `${prefix}_${safeEmail}_${stamp}.json`
  const blob = new Blob([JSON.stringify(redacted, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
